<template>
  <div class="m-auto">
    <div
      class="flex flex-col text-center"
      :class="[ alignLeft ? 'items-start' : 'items-center' ]"
    >
      <div class="flex border border-EXPONDO-grey-20 bg-EXPONDO-white rounded-md">
        <QuantitySelectorButton
          v-if="hasControls"
          :disabled="isDisable || !canDecrement"
          :is-left="true"
          :is-small="hasSmallButtons"
          :is-large="hasLargeButtons"
          data-testid="quantity-decrement"
          @click="addQty(-1)"
        >
          -
        </QuantitySelectorButton>
        <SfInput
          type="number"
          :name="name"
          :model-value="localQty"
          :min="minQuantity"
          v-bind="$attrs"
          :disabled="isDisable"
          :label="$t('Quantity')"
          class="sf-quantity-selector sf-quantity-selector__input my-auto"
          :class="{
            'rounded-md': !hasControls,
            'sf-quantity-selector--narrow': hasSmallButtons || hasLargeButtons
          }"
          autocomplete="off"
          data-testid="quantity-input"
          @update:model-value="inputQty"
          @blur="blurQty($event.target)"
        />
        <QuantitySelectorButton
          v-if="hasControls"
          :disabled="isDisable || !canIncrement"
          :is-left="false"
          :is-small="hasSmallButtons"
          :is-large="hasLargeButtons"
          data-testid="quantity-increment"
          @click="addQty(1)"
        >
          +
        </QuantitySelectorButton>
      </div>
      <div
        v-if="warningOnQuantity"
        class="text-EXPONDO-red pt-1"
        :class="{ 'text-left': alignLeft }"
        data-testid="quantity-message"
      >
        {{ $t('You have reached the maximum quantity') }}
      </div>
    </div>
  </div>
</template>
<script lang="ts">

import debounce from 'lodash/debounce.js';
import { DEBOUNCE_TIMES } from '~~/constants/debounceTimes';
import { QUANTITY_ACTION_TYPE } from '~~/constants/googleTagManager';
import type { QuantityActionType } from '~~/constants/googleTagManager';
import { MAX_SINGLE_PRODUCT_CART_QUANTITY } from '~~/constants/checkout';

export default defineComponent({
  name: 'QuantitySelector',

  inheritAttrs: false,
  props: {
    /** Quantity */
    qty: {
      type: Number,
      required: true,
    },
    minQuantity: {
      type: Number,
      default: 1,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    stock: {
      type: Number,
      default: 1,
    },
    hasControls: {
      type: Boolean,
      default: false,
    },
    alignLeft: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'default',
      validator: (value: string) => {

        return ['default', 'smallButtons', 'largeButtons'].includes(value);

      },
    },
    isDebounced: {
      type: Boolean,
      default: true,
    },
    sku: {
      type: String,
      required: true,
    },
    triggeredIn: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    disableMaxQuantityValidation: {
      type: Boolean,
      default: false,
    },
    disableQuantityChangeEvent: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input'],
  setup(props, { emit }) {

    const { qty, minQuantity } = toRefs(props);
    const { trackQuantityChange } = useGtmEvents();

    const localQty: Ref<typeof props['qty']> = ref(qty.value);
    const warningOnQuantity = ref<boolean>(false);
    const canIncrement = ref<boolean>(true);
    const canDecrement = ref<boolean>(localQty.value > minQuantity.value);
    const hasSmallButtons = computed(() => props?.mode === 'smallButtons');
    const hasLargeButtons = computed(() => props?.mode === 'largeButtons');
    const { isExtraGuaranteeOnlyCart } = useCartExtended();

    const validateInput = (newQuantity: number) => {

      warningOnQuantity.value = false;
      canIncrement.value = true;
      canDecrement.value = true;

      if (newQuantity <= minQuantity.value || isNaN(newQuantity)) {

        canDecrement.value = false;

        return minQuantity.value;

      }

      if (props.disableMaxQuantityValidation) {

        return newQuantity;

      }

      if (newQuantity > props.stock && props.stock < MAX_SINGLE_PRODUCT_CART_QUANTITY) {

        warningOnQuantity.value = true;
        canIncrement.value = false;

        return props.stock;

      } else if (newQuantity > MAX_SINGLE_PRODUCT_CART_QUANTITY) {

        warningOnQuantity.value = true;
        canIncrement.value = false;

        // TODO: INSP-337- display information about max quantity to user
        return MAX_SINGLE_PRODUCT_CART_QUANTITY;

      }

      return newQuantity;

    };

    const callQuantityChangeEvent = (actionType: QuantityActionType) => {

      trackQuantityChange({
        sku: props.sku,
        pageCategory: props.triggeredIn,
        actionType,
        selectedQuantity: localQty.value,
      });

    };

    const modifyQuantityWithValidationNoDebounce = (newQuantity: number, actionType: QuantityActionType) => {

      localQty.value = validateInput(newQuantity);

      if (!props.disableQuantityChangeEvent) {

        callQuantityChangeEvent(actionType);

      }
      emit('input', localQty.value);

    };
    const modifyQuantityWithValidation = debounce(
      modifyQuantityWithValidationNoDebounce,
      DEBOUNCE_TIMES.NUMBER_INPUT,
    );

    const inputQty = (input: string) => {

      const parsedQty = parseInt(input);

      localQty.value = parsedQty;

      if (!props.isDebounced) {

        if (!Number.isNaN(localQty.value)) {

          canDecrement.value = localQty.value > props.minQuantity;
          emit('input', localQty.value);

        }

        return;

      }

      modifyQuantityWithValidation(localQty.value, QUANTITY_ACTION_TYPE.TEXT_INPUT);

    };

    const blurQty = (input: HTMLInputElement) => {

      if (props.isDebounced) {

        return;

      }
      const newAmount = parseInt(input.value);
      modifyQuantityWithValidationNoDebounce(newAmount, QUANTITY_ACTION_TYPE.TEXT_INPUT);

    };

    const addQty = (amount: number) => {

      localQty.value += amount;
      modifyQuantityWithValidationNoDebounce(
        localQty.value,
        amount > 0 ? QUANTITY_ACTION_TYPE.INCREMENT : QUANTITY_ACTION_TYPE.DECREMENT,
      );

    };

    watch(qty,
      () => {

        if (qty.value !== localQty.value) {

          localQty.value = qty.value;

        }

      },
      { deep: true });

    const isDisable = computed(() => props.disabled || isExtraGuaranteeOnlyCart.value === true);

    return {
      warningOnQuantity,
      localQty,
      validateInput,
      inputQty,
      blurQty,
      addQty,
      canIncrement,
      canDecrement,
      hasSmallButtons,
      hasLargeButtons,
      isDisable,
    };

  },
});

</script>
<style lang="scss" scoped>
@use "~/assets/scss/helpers/_layout" as layout;
@use "~/assets/scss/helpers/_typography" as typography;
.sf-quantity-selector {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: var(--quantity-selector-justify-content);
  box-sizing: border-box;
  height: var(--quantity-selector-height, 3.25rem);
  width: var(--quantity-selector-width, 6.75rem);
  background: var(--quantity-selector-background, var(--c-light));

  @include typography.font(
    --quantity-selector-font,
    var(--font-weight--normal),
    var(--font-size--lg),
    1.6,
    var(--font-family--primary)
  );
  @include layout.border(--quantity-selector-border, 0, solid, var(--c-light));

  &__button {
    --button-height: 100%;
    --button-padding: var(--spacer-2xs) var(--spacer-xs) 0;
    --button-background: transparent;
    --button-color: var(--c-text);

    &:hover {
      --button-background: transparent;
      --button-box-shadow: none;

      text-shadow: var(--quantity-selector-text-shadow, 0 4px 4px rgba(0, 0, 0, 0.25));
    }
  }

  &__input {
    --input-bar-display: none;
    --input-height: var(--quantity-selector-height, 3.25rem);
    --input-padding: 0;
    --input-margin: 0;
    --input-border: 0;
    --input-text-align: center;

    & input {
      font-family: inherit;
    }

    @apply bg-EXPONDO-white;
  }

  &--secondary {
    --quantity-selector-background: transparent;
    --quantity-selector-border-width: 0 0 1px 0;
    --quantity-selector-width: 100%;
    --quantity-selector-justify-content: center;
    --quantity-selector-text-shadow: none;

    .sf-quantity-selector__button {
      --button-background: var(--c-light);
      --button-border-radius: 100%;
      --button-padding: 0;
      --button-size: 1.625rem;

      flex: none;
    }

    .sf-quantity-selector__input {
      --input-width: 9rem;
    }
  }

  &--transparent {
    --quantity-selector-background: transparent;
  }

  &--narrow {
    --quantity-selector-width: 2.25rem;
    --quantity-selector-height: 1.5rem;
    --input-font-size: var(--font-size--sm);
  }

  :deep(.sf-input__label) {
    @apply sr-only text-EXPONDO-black;
  }
}
</style>
